import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Star, Camera, CheckCircle2, Loader2, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { reviewService } from '../../services/ReviewService';
import { ReviewToken, Product } from '../../types';

interface ReviewFormModalProps {
    token: string;
    onClose: () => void;
}

export const ReviewFormModal: React.FC<ReviewFormModalProps> = ({ token, onClose }) => {
    // Validation State
    const [isValidating, setIsValidating] = useState(true);
    const [tokenData, setTokenData] = useState<ReviewToken | null>(null);
    const [validationError, setValidationError] = useState<string | null>(null);

    // Form State
    const [rating, setRating] = useState(0);
    const [hoverRating, setHoverRating] = useState(0);
    const [customerName, setCustomerName] = useState('');
    const [comment, setComment] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [filePreviews, setFilePreviews] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const validateToken = async () => {
            setIsValidating(true);
            try {
                const data = await reviewService.validateToken(token);
                if (data) {
                    setTokenData(data);
                } else {
                    setValidationError('Este enlace es inválido, ya fue utilizado o ha caducado.');
                }
            } catch (error) {
                setValidationError('Error al validar el enlace. Intenta nuevamente.');
            } finally {
                setIsValidating(false);
            }
        };

        validateToken();
    }, [token]);

    // Handle file selection and preview generation
    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            const selectedFiles = Array.from(e.target.files);
            
            // Limit to 3 files
            if (files.length + selectedFiles.length > 3) {
                alert('Solo puedes subir un máximo de 3 imágenes.');
                return;
            }

            const validFiles = selectedFiles.filter(file => {
                const isValidType = file.type.startsWith('image/');
                const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB
                if (!isValidType) alert(`${file.name} no es una imagen válida.`);
                if (!isValidSize) alert(`${file.name} supera el límite de 5MB.`);
                return isValidType && isValidSize;
            });

            setFiles(prev => [...prev, ...validFiles]);

            // Generate previews
            validFiles.forEach(file => {
                const reader = new FileReader();
                reader.onloadend = () => {
                    setFilePreviews(prev => [...prev, reader.result as string]);
                };
                reader.readAsDataURL(file);
            });
        }
    };

    const removeFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
        setFilePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!tokenData) return;
        if (rating === 0) {
            alert('Por favor selecciona una calificación de estrellas.');
            return;
        }

        setIsSubmitting(true);
        try {
            // Sanitize images first to WebP to remove EXIF and enforce security
            const sanitizedFiles = await Promise.all(
                files.map(file => reviewService.sanitizeImage(file))
            );

            const success = await reviewService.submitReview(
                tokenData.id,
                customerName,
                rating,
                comment,
                sanitizedFiles
            );

            if (success) {
                setIsSuccess(true);
            } else {
                alert('Ocurrió un error al enviar tu review. Por favor intenta nuevamente.');
            }
        } catch (error) {
            console.error('Submit error:', error);
            alert('Error inesperado al enviar la review.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Body scroll lock
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = 'unset'; };
    }, []);

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
                {/* Backdrop */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="absolute inset-0 bg-black/80 backdrop-blur-md"
                    onClick={() => !isSubmitting && !isSuccess && onClose()}
                />

                {/* Modal Container */}
                <motion.div
                    initial={{ opacity: 0, y: 50, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 20, scale: 0.95 }}
                    transition={{ type: "spring", damping: 25, stiffness: 300 }}
                    className="relative w-full max-w-lg bg-zinc-950 border border-zinc-800 rounded-lg shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
                >
                    {/* Header */}
                    <div className="flex justify-between items-center p-6 border-b border-zinc-900 bg-zinc-950/50 backdrop-blur-sm relative z-10 shrink-0">
                        <h2 className="text-xl font-display font-bold uppercase italic text-white">Tu Experiencia</h2>
                        {!isSubmitting && !isSuccess && (
                            <button onClick={onClose} className="p-2 text-zinc-500 hover:text-white transition-colors rounded-full hover:bg-zinc-900">
                                <X size={20} />
                            </button>
                        )}
                    </div>

                    {/* Content Scrollable Area */}
                    <div className="overflow-y-auto overflow-x-hidden p-6 flex-grow custom-scrollbar">
                        {isValidating ? (
                            <div className="flex flex-col items-center justify-center py-20 text-zinc-400">
                                <Loader2 size={32} className="animate-spin mb-4" />
                                <p className="font-mono text-xs uppercase tracking-widest">Validando enlace seguro...</p>
                            </div>
                        ) : validationError ? (
                            <div className="flex flex-col items-center justify-center py-16 text-center">
                                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mb-4">
                                    <AlertCircle size={32} className="text-red-500" />
                                </div>
                                <h3 className="text-lg font-bold text-white mb-2">Enlace no válido</h3>
                                <p className="text-zinc-500 text-sm max-w-[250px]">{validationError}</p>
                                <button
                                    onClick={onClose}
                                    className="mt-8 px-6 py-3 bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm font-bold uppercase tracking-widest text-xs transition-colors"
                                >
                                    Cerrar
                                </button>
                            </div>
                        ) : isSuccess ? (
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="flex flex-col items-center justify-center py-16 text-center"
                            >
                                <motion.div 
                                    initial={{ scale: 0 }}
                                    animate={{ scale: 1 }}
                                    transition={{ type: "spring", delay: 0.2 }}
                                    className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mb-6"
                                >
                                    <CheckCircle2 size={40} className="text-green-500" />
                                </motion.div>
                                <h3 className="text-2xl font-display font-black italic uppercase text-white mb-2">¡Gracias por tu review!</h3>
                                <p className="text-zinc-400 text-sm max-w-[300px] leading-relaxed">
                                    Tu comentario ha sido enviado exitosamente y será publicado pronto. ¡Esperamos que disfrutes tu compra!
                                </p>
                                <button
                                    onClick={onClose}
                                    className="mt-8 px-8 py-3 bg-white text-black hover:bg-zinc-200 rounded-sm font-black uppercase tracking-widest text-xs shadow-[0_0_20px_rgba(255,255,255,0.3)] transition-all active:scale-95"
                                >
                                    Cerrar y Volver
                                </button>
                            </motion.div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Rating Selector */}
                                <div className="flex flex-col items-center py-4">
                                    <p className="text-xs font-bold text-white uppercase tracking-widest mb-4">¿Cómo calificarías tu experiencia?</p>
                                    <div className="flex gap-2">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                key={star}
                                                type="button"
                                                onMouseEnter={() => setHoverRating(star)}
                                                onMouseLeave={() => setHoverRating(0)}
                                                onClick={() => setRating(star)}
                                                className="p-1 focus:outline-none transition-transform hover:scale-110 active:scale-95"
                                            >
                                                <Star
                                                    size={40}
                                                    className={`transition-colors duration-200 ${
                                                        star <= (hoverRating || rating)
                                                            ? 'fill-white text-white drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]'
                                                            : 'fill-transparent text-zinc-700'
                                                    }`}
                                                />
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Inputs */}
                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Tu Nombre</label>
                                        <input
                                            type="text"
                                            required
                                            maxLength={50}
                                            value={customerName}
                                            onChange={(e) => setCustomerName(e.target.value)}
                                            placeholder="Ej. Carlos M."
                                            className="w-full bg-zinc-900 border border-zinc-800 text-white px-4 py-3 rounded-sm focus:border-white focus:outline-none transition-colors text-sm"
                                        />
                                    </div>
                                    <div>
                                        <div className="flex justify-between mb-2">
                                            <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500">¿Qué artículos compraste y qué te parecieron?</label>
                                            <span className="text-[10px] text-zinc-600 font-mono">{comment.length}/500</span>
                                        </div>
                                        <textarea
                                            required
                                            maxLength={500}
                                            rows={4}
                                            value={comment}
                                            onChange={(e) => setComment(e.target.value)}
                                            placeholder="Ej. Compré un case Forjado para iPhone 15 Pro y la calidad es excelente..."
                                            className="w-full bg-zinc-900 border border-zinc-800 text-white px-4 py-3 rounded-sm focus:border-white focus:outline-none transition-colors resize-none text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Media Uploader */}
                                <div>
                                    <label className="block text-xs font-mono uppercase tracking-widest text-zinc-500 mb-2">Fotos (Opcional, máx 3)</label>
                                    <div className="grid grid-cols-4 gap-2">
                                        {/* Previews */}
                                        {filePreviews.map((preview, idx) => (
                                            <div key={idx} className="relative aspect-square rounded-sm overflow-hidden border border-zinc-700 group">
                                                <img src={preview} alt={`Preview ${idx}`} className="w-full h-full object-cover" />
                                                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFile(idx)}
                                                        className="p-1 bg-red-500 text-white rounded-full hover:scale-110 transition-transform"
                                                    >
                                                        <X size={14} />
                                                    </button>
                                                </div>
                                            </div>
                                        ))}

                                        {/* Upload Button */}
                                        {files.length < 3 && (
                                            <button
                                                type="button"
                                                onClick={() => fileInputRef.current?.click()}
                                                className="aspect-square flex flex-col items-center justify-center gap-2 border border-dashed border-zinc-700 hover:border-white hover:bg-zinc-900 text-zinc-500 hover:text-white transition-all rounded-sm"
                                            >
                                                <Camera size={20} />
                                                <span className="text-[10px] uppercase font-bold text-center px-1">Añadir Foto</span>
                                            </button>
                                        )}
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            multiple
                                            onChange={handleFileSelect}
                                            className="hidden"
                                        />
                                    </div>
                                    <p className="text-[10px] text-zinc-600 mt-2">Solo JPG, PNG o WebP. Máx 5MB.</p>
                                </div>

                                {/* Submit Button */}
                                <button
                                    type="submit"
                                    disabled={isSubmitting || rating === 0 || !customerName.trim()}
                                    className="w-full bg-white text-black font-black uppercase tracking-widest py-4 rounded-sm transition-all hover:bg-zinc-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-[0_0_15px_rgba(255,255,255,0.2)] disabled:shadow-none flex items-center justify-center gap-2"
                                >
                                    {isSubmitting ? (
                                        <>
                                            <Loader2 size={18} className="animate-spin" />
                                            Enviando Seguro...
                                        </>
                                    ) : (
                                        'Publicar Review'
                                    )}
                                </button>
                            </form>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};
